import { Button, ListItemIcon, MenuItem } from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';
import { FunctionComponent } from 'react';
import {
  IGeneratedSecrReportResponse,
  IGhgDetails,
  IGhgTable,
  IReductionDisclosures,
  ISecrEmissionData,
  ISecrReportFiguresGroup,
} from '../../../shared/services/generated-report/generated-report.model';
import type { SecrSourceMetricField } from '../reporting-components/secr/secr-source-applicability';
import { formatSecrSourceDataCell, formatSecrSourceTotalCell } from '../reporting-components/secr/secr-source-matrix-display';
import {
  buildSecrSourceTotalLine,
  displaySourceLabel,
  shouldShowUnmappedRow,
} from '../reporting-components/secr/secr-source-matrix.utils';
import { SECR_EM_DASH, formatSecrReportNumber } from '../reporting-components/secr/secr-number-format';
import { hasScopeThreeData } from '../reporting-components/secr/secr-summary-table.utils';

const csvEscape = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
const csvCell = (v: string | number | null | undefined) => csvEscape(v == null ? '' : String(v));

function pushCsvRow(csvRows: string[], cells: Array<string | number | null | undefined>): void {
  csvRows.push(cells.map((c) => csvCell(c)).join(','));
}

function csvNumericCell(v: number | null | undefined): string {
  if (v == null || Number.isNaN(Number(v))) {
    return SECR_EM_DASH;
  }
  return formatSecrReportNumber(Number(v));
}

function periodHeader(includeGlobal: boolean): string {
  return includeGlobal
    ? 'Report Period Name,Start Date,End Date,Section,Line,UK,Overseas,Total'
    : 'Report Period Name,Start Date,End Date,Section,Line,UK';
}

function periodRowPrefix(d: ISecrReportFiguresGroup): [string, string, string] {
  return [d.reportPeriodName, String(d.startDate), String(d.endDate)];
}

function pushSummaryRows(csvRows: string[], report: IGeneratedSecrReportResponse, d: ISecrReportFiguresGroup): void {
  const rows: { label: string; data: ISecrEmissionData | undefined }[] = [
    { label: 'Scope 1 (tCO2e)', data: d.emissions.scopeOne },
    { label: 'Scope 2 (Location-based) (tCO2e)', data: d.emissions.scopeTwoLocation },
    { label: 'Scope 2 (Market-based) (tCO2e)', data: d.emissions.scopeTwoMarket },
  ];
  if (report.includeScopeThree && hasScopeThreeData(d.emissions.scopeThree)) {
    rows.push({ label: 'Scope 3 (tCO2e)', data: d.emissions.scopeThree });
  }
  rows.push(
    { label: 'Total emissions (Location-based) (tCO2e)', data: d.emissions.totalLocation },
    { label: 'Total emissions (Market-based) (tCO2e)', data: d.emissions.totalMarket },
    { label: 'Energy consumption (kWh)', data: d.kwh }
  );

  const p = periodRowPrefix(d);
  for (const row of rows) {
    const cols: Array<string> = [...p, 'Summary', row.label, csvNumericCell(row.data?.uk)];
    if (report.includeGlobal) {
      cols.push(csvNumericCell(row.data?.overseas), csvNumericCell(row.data?.global));
    }
    pushCsvRow(csvRows, cols);
  }
}

// Derives the CSV label from the ratio key's semantics (scope set + denominator), so the
// label set isn't a brittle exhaustive switch and tolerates any revenue/employee ratio the
// settings catalog enables. Keys not following the convention fall back to the raw key.
function intensityRatioCsvLabel(key: string, currencySymbol: string): string {
  const scopeSuffix = key.startsWith('scope_one_two') ? '(Scope 1&2)' : '(total)';
  if (key.includes('revenue')) {
    return `tCO2e per ${currencySymbol}m revenue ${scopeSuffix}`;
  }
  if (key.includes('employee')) {
    return `tCO2e per employee ${scopeSuffix}`;
  }
  return key;
}

function pushIntensityRows(csvRows: string[], report: IGeneratedSecrReportResponse, d: ISecrReportFiguresGroup): void {
  if (!d.intensityRatios?.length) return;
  const p = periodRowPrefix(d);
  for (const ratio of d.intensityRatios) {
    const cols: Array<string> = [
      ...p,
      'Intensity ratios (Location-based)',
      intensityRatioCsvLabel(ratio.intensityRatio, report.currencySymbol ?? '£'),
      csvNumericCell(ratio.emissions?.uk),
    ];
    if (report.includeGlobal) {
      cols.push(csvNumericCell(ratio.emissions?.overseas), csvNumericCell(ratio.emissions?.global));
    }
    pushCsvRow(csvRows, cols);
  }
}

function pushSourceBreakdownRows(csvRows: string[], report: IGeneratedSecrReportResponse, d: ISecrReportFiguresGroup): void {
  if (report.includeEnergyCategoryBreakdown === false) return;
  const raw = d.energyBreakdownBySource || [];
  if (!raw.length) return;
  const dataLines = raw.filter((l) => l.key !== 'total').filter(shouldShowUnmappedRow);
  const linesForExport = [...dataLines, buildSecrSourceTotalLine(dataLines)];
  const metrics: { label: string; field: SecrSourceMetricField }[] = [
    { label: 'Energy (kWh)', field: 'energyKwh' },
    { label: 'Scope 1 (tCO2e)', field: 'scope1Tco2e' },
    { label: 'Scope 2 (Location-based) (tCO2e)', field: 'scope2LocationTco2e' },
    { label: 'Scope 2 (Market-based) (tCO2e)', field: 'scope2MarketTco2e' },
  ];

  const p = periodRowPrefix(d);
  for (const line of linesForExport) {
    const sourceLabel = line.key === 'total' ? 'Total' : displaySourceLabel(line.key, line.label);
    for (const { label: metricLabel, field } of metrics) {
      const isTotal = line.key === 'total';
      const uk = isTotal
        ? formatSecrSourceTotalCell(dataLines, field, line.uk[field])
        : formatSecrSourceDataCell(line.key, field, line.uk[field]);
      const cols = [
        ...p,
        'Energy and emissions by source (Scope 1 & 2)',
        `${sourceLabel} - ${metricLabel}`,
        uk,
      ];
      if (report.includeGlobal) {
        const overseas = isTotal
          ? formatSecrSourceTotalCell(dataLines, field, line.overseas[field])
          : formatSecrSourceDataCell(line.key, field, line.overseas[field]);
        const total = isTotal
          ? formatSecrSourceTotalCell(dataLines, field, line.global[field])
          : formatSecrSourceDataCell(line.key, field, line.global[field]);
        cols.push(overseas, total);
      }
      pushCsvRow(csvRows, cols);
    }
  }
}

const GHG_LONG_HEADERS = ['Report period', 'Line item', 'tCO2e'] as const;

function ghgEmissionForPeriod(d: IGhgDetails, which: 'base' | 'previous' | 'current'): number | null | undefined {
  if (which === 'base') return d.baseYearEmission;
  if (which === 'previous') return d.previousYearEmission;
  return d.currentYearEmission;
}

function ghgDistinctYearSlots(table: IGhgTable): { year: string; which: 'base' | 'previous' | 'current' }[] {
  const ordered: { year: string; which: 'base' | 'previous' | 'current' }[] = [
    { year: table.reportPeriods.base, which: 'base' },
  ];
  if (table.reportPeriods.previous) {
    ordered.push({ year: table.reportPeriods.previous, which: 'previous' });
  }
  ordered.push({ year: table.reportPeriods.current, which: 'current' });

  const whichByYear = new Map<string, 'base' | 'previous' | 'current'>();
  for (const slot of ordered) {
    whichByYear.set(slot.year, slot.which);
  }

  const sortKey = (y: string): number => {
    const m = /\d{4}/.exec(y);
    return m ? parseInt(m[0], 10) : 0;
  };

  return Array.from(whichByYear.keys()).sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka !== kb) return ka - kb;
    return a.localeCompare(b);
  }).map((year) => ({ year, which: whichByYear.get(year)! }));
}

function pushGhgLongFormatRows(csvRows: string[], table: IGhgTable): void {
  pushCsvRow(csvRows, [...GHG_LONG_HEADERS]);

  const summaryLines: { label: string; details: IGhgDetails }[] = [
    { label: 'Scope 1 (tCO2e)', details: table.scopeOne },
    { label: 'Scope 2 (market based) (tCO2e)', details: table.scopeTwoMarket },
    { label: 'Scope 2 (location based) (tCO2e)', details: table.scopeTwoLocation },
    { label: 'Scope 3 (tCO2e)', details: table.scopeThree },
    { label: 'Total (market based) (tCO2e)', details: table.totalMarket },
    { label: 'Total (location based) (tCO2e)', details: table.totalLocation },
  ];

  const yearSlots = ghgDistinctYearSlots(table);
  for (const { year, which } of yearSlots) {
    for (const { label, details } of summaryLines) {
      pushCsvRow(csvRows, [year, label, csvNumericCell(ghgEmissionForPeriod(details, which))]);
    }
    for (const group of table.breakdownPerGroup ?? []) {
      const groupDetails: IGhgDetails = {
        baseYearEmission: group.baseYearEmission,
        previousYearEmission: group.previousYearEmission,
        currentYearEmission: group.currentYearEmission,
      };
      const groupLabel = group.scope === '2' ? `${group.groupName} (location-based)` : group.groupName;
      pushCsvRow(csvRows, [year, `${groupLabel} (tCO2e)`, csvNumericCell(ghgEmissionForPeriod(groupDetails, which))]);
    }
  }
}

// Supplementary reduction disclosures (targets/plan coverage + offsets register), appended as
// their own labelled blocks. Reported totals stay gross; the net line is an informational memo.
function pushReductionDisclosureRows(csvRows: string[], disclosures: IReductionDisclosures | null | undefined): void {
  if (!disclosures) return;
  const { targets, planCoverage, offsets } = disclosures;

  if (targets || planCoverage) {
    pushCsvRow(csvRows, []);
    pushCsvRow(csvRows, ['Reduction targets and progress']);
    if (targets?.settings) {
      pushCsvRow(csvRows, ['Base year', targets.settings.baseYear]);
      pushCsvRow(csvRows, ['Target year', targets.settings.targetYear]);
      if (targets.settings.annualGrowthPercentage != null && targets.settings.annualGrowthPercentage !== 0) {
        pushCsvRow(csvRows, ['Annual growth assumption (%)', targets.settings.annualGrowthPercentage]);
      }
    }
    if (targets?.metrics) {
      const m = targets.metrics;
      if (m.latestActualValue != null) pushCsvRow(csvRows, ['Latest reported total (tCO2e)', csvNumericCell(m.latestActualValue)]);
      if (m.finalTargetValue != null) pushCsvRow(csvRows, ['Final target (tCO2e)', csvNumericCell(m.finalTargetValue)]);
      if (m.reductionToTarget != null) pushCsvRow(csvRows, ['Reduction still required (tCO2e)', csvNumericCell(m.reductionToTarget)]);
      if (m.plannedReductionByTarget != null) pushCsvRow(csvRows, ['Planned reduction by target year (tCO2e)', csvNumericCell(m.plannedReductionByTarget)]);
      if (m.gapToTarget != null) pushCsvRow(csvRows, ['Gap not covered by planned measures (tCO2e)', csvNumericCell(m.gapToTarget)]);
    }
    if (planCoverage) {
      pushCsvRow(csvRows, ['Committed measures', planCoverage.totalActions]);
      pushCsvRow(csvRows, ['Completed measures', planCoverage.completedActions]);
      pushCsvRow(csvRows, ['Quantified measures', planCoverage.quantifiedActions]);
      pushCsvRow(csvRows, ['Quantified annual reduction (tCO2e)', csvNumericCell(planCoverage.quantifiedAnnualReductionTco2e)]);
    }
  }

  if (offsets && offsets.rows.length > 0) {
    pushCsvRow(csvRows, []);
    pushCsvRow(csvRows, ['Carbon offsets (supplementary; not deducted from any scope total)']);
    pushCsvRow(csvRows, ['Name', 'Type', 'Vintage', 'Registry', 'Standard', 'Serial range', 'Project reference', 'Retirement status', 'Retirement date', 'tCO2e', 'Certificate held']);
    for (const row of offsets.rows) {
      pushCsvRow(csvRows, [
        row.name,
        row.offsetType,
        row.vintage,
        row.registry,
        row.standard,
        row.serialRange,
        row.projectReference,
        row.retirementStatus,
        row.retirementDate,
        csvNumericCell(row.amountTCE),
        row.hasCertificate ? 'Yes' : 'No',
      ]);
    }
    pushCsvRow(csvRows, ['Total', null, null, null, null, null, null, null, null, csvNumericCell(offsets.totalTco2e), null]);
    if (offsets.netMemo) {
      pushCsvRow(csvRows, [`Informational memo (${offsets.netMemo.basis}): net of offsets`, null, null, null, null, null, null, null, null, csvNumericCell(offsets.netMemo.netTco2e), null]);
    }
  }
}

function convertToCSVGhg(table: IGhgTable): string {
  const csvRows: string[] = [];
  pushGhgLongFormatRows(csvRows, table);
  pushReductionDisclosureRows(csvRows, table.reductionDisclosures);
  return csvRows.join('\r\n');
}

export const DownloadCSV: FunctionComponent<{
  data: IGeneratedSecrReportResponse | IGhgTable;
  fileName: string;
  fileType: 'secr' | 'ghg';
  disabled?: boolean;
  // 'menuItem' (default) renders the original menu entry for use inside a download Menu;
  // 'button' renders a standalone outlined button for one-click downloads.
  displayAs?: 'menuItem' | 'button';
}> = ({ data, fileType, fileName, disabled = false, displayAs = 'menuItem' }) => {
  const convertToCSVSecr = (obj: IGeneratedSecrReportResponse) => {
    const csvRows: string[] = [periodHeader(!!obj.includeGlobal)];

    for (const d of obj.reportDetails) {
      pushSummaryRows(csvRows, obj, d);
      pushIntensityRows(csvRows, obj, d);
      pushSourceBreakdownRows(csvRows, obj, d);
    }
    pushReductionDisclosureRows(csvRows, obj.reductionDisclosures);
    return csvRows.join('\r\n');
  };

  const downloadCSV = () => {
    const csvBody =
      fileType === 'secr'
        ? convertToCSVSecr(data as IGeneratedSecrReportResponse)
        : fileType === 'ghg'
          ? convertToCSVGhg(data as IGhgTable)
          : null;
    if (csvBody == null) return;
    const csvWithBom = `\uFEFF${csvBody}`;
    const csvData = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const csvURL = URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (displayAs === 'button') {
    return (
      <Button variant='outlined' startIcon={<DownloadOutlined fontSize='small' />} disabled={disabled} onClick={downloadCSV}>
        Download CSV
      </Button>
    );
  }

  return (
    <MenuItem disabled={disabled} onClick={downloadCSV}>
      <ListItemIcon><DownloadOutlined fontSize='small' /></ListItemIcon>
      CSV (.csv)
    </MenuItem>
  );
};
